import { useEffect, useId, useRef } from 'react';
import { createStore, useStore } from 'src/utils/createStore';

export type LessonFlowHeaderOverrides = {
  fraction?: number;
  onBack?: () => void;
};

const EMPTY: LessonFlowHeaderOverrides = {};

const headerStore = createStore<{ overrides: LessonFlowHeaderOverrides; owner: string | null }>({
  overrides: EMPTY,
  owner: null,
});

const emit = (next: LessonFlowHeaderOverrides, from: string) => {
  headerStore.setState({ overrides: next, owner: from });
};

/** The outgoing screen unmounts after the incoming one wrote: only the slot holder may empty it. */
const release = (from: string) => {
  if (headerStore.getState().owner !== from) return;
  headerStore.setState({ overrides: EMPTY, owner: null });
};

const selectOverrides = (state: { overrides: LessonFlowHeaderOverrides }) => state.overrides;

export const useLessonFlowHeaderOverrides = (): LessonFlowHeaderOverrides =>
  useStore(headerStore, selectOverrides);

export const useLessonFlowHeader = ({
  fraction,
  onBack,
}: LessonFlowHeaderOverrides) => {
  const id = useId();
  const onBackRef = useRef(onBack);
  useEffect(() => {
    onBackRef.current = onBack;
  });
  const hasBack = Boolean(onBack);

  useEffect(() => {
    emit(
      { fraction, onBack: hasBack ? () => onBackRef.current?.() : undefined },
      id,
    );
    return () => release(id);
  }, [fraction, hasBack, id]);
};
